// src/AppRouter.js
//
// El router NO conoce ningún módulo por dentro: solo consume los arrays de
// rutas que cada módulo publica desde su index.js, agregados en app/routes.js.
// Para sumar un módulo, editá ese registry — este archivo no debería cambiar.
import React, { useEffect, useContext, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { isTokenExpired } from './utils/services/Auth/isTokenExpired';
import { AuthContext } from './context/AuthContext/AuthContext';
import LoadingSpinner from './components/Advanced/Feedback/LoadingSpinner';
import { PatientModalProvider } from './context/PatientModalContext/PatientModalContext';
import { ObservationsModalProvider } from './context/ObservationsModalContext/ObservationsModalContext';
import ProtectedRoute from './utils/Protected/ProtectedRoute';
import Layout from './app/Layout/Layout';
import { RoleGuard } from './app/AccessDenied';
import { publicRoutes, protectedRoutes } from './app/routes';

// Cada módulo publica sus páginas con React.lazy: su código no viaja en el
// bundle inicial, se descarga al entrar a la ruta. El Suspense va por ruta (y no
// envolviendo al Layout) para que el sidebar no parpadee en cada navegación.
const withSuspense = (element) => (
  <Suspense fallback={<LoadingSpinner message="Loading..." />}>{element}</Suspense>
);

const AppRouter = () => {
  const { loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Token stays in localStorage for API compatibility
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!loading && (!token || isTokenExpired(token))) {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      const publicPaths = ['/Login', '/ChangePassword', '/auth/change-password'];
      if (!publicPaths.includes(location.pathname)) {
        navigate('/Login');
      }
    }
  }, [loading, location.pathname, navigate]);

  if (loading) return <LoadingSpinner message="Checking session..." />;

  return (
    <PatientModalProvider>
      <ObservationsModalProvider>
        <Routes>
          {publicRoutes.map((r) => (
            <Route key={r.path} path={r.path} element={withSuspense(r.element)} />
          ))}

          <Route element={<ProtectedRoute />}>
            <Route element={<Layout />}>
              {protectedRoutes.map((r) => (
                <Route
                  key={r.path}
                  path={r.path}
                  element={withSuspense(r.noRoleGuard ? r.element : <RoleGuard>{r.element}</RoleGuard>)}
                />
              ))}
            </Route>
          </Route>
        </Routes>
      </ObservationsModalProvider>
    </PatientModalProvider>
  );
};

export default AppRouter;
